/**
 * 直播营销管理 - 接口定义
 * 迁移自 conversation/src/api/data.js
 *   - rpaLiveVideo / rpaLiveList / rpaAppList
 *   - rpaSwitchList / rpaIndex / rpaAdd / rpaEdit / rpaDelete
 *   - rpaNewFansList / rpaRoomList / rpaProspectList / rpaCommentLimit
 */
import { apiGet, apiPost } from '@/utils/request';
import type {
  LiveAccountItem,
  LiveCommentScene,
  LiveConfigRecord,
  LiveDamuItem,
  LiveDamuParams,
  LiveFansRing,
  LiveListParams,
  LivePaginatedResponse,
  LiveProvinceItem,
  LiveReachUser,
  LiveReplyLimit,
  LiveRpaParams,
  LiveRpaSwitchListResponse,
  LiveReviewHeader,
  LiveReviewListResponse,
  LiveRoomItem,
  LiveSourceGroup,
  LiveStatColumnList,
  LiveStatExportResponse,
  LiveStatListResponse,
  LiveStatParams,
  LiveUserTimeline,
  LiveUserTimelineParams,
  LiveVideoProspectUser,
} from '@/types/live';

export type { LiveRpaParams } from '@/types/live';
export type { LiveDamuParams, LiveDamuItem } from '@/types/live';
export type { LiveUserTimelineParams, LiveUserTimeline } from '@/types/live';
export type {
  LiveStatParams,
  LiveStatColumnList,
  LiveStatListResponse,
  LiveStatExportResponse,
} from '@/types/live';
/** LiveAccountListResponse 与 liveAccountApi 同源，本文件内即可，无需 re-export */

/**
 * 统一解包 Laravel 分页格式
 *
 * 后端 Rpa 列表接口响应（拦截器已解外层后）：
 *   Laravel 格式：{ data: [...], total, current_page, last_page, per_page, links, ... }
 *   扁平格式：    { data: [...], total }
 *   裸数组：      [...]
 *
 * 兼容三种格式，返回统一结构 { data: T[], total: number }，调用方按 res.data / res.total 取值
 */
function unwrapLaravelPaginated<T>(raw: unknown): LivePaginatedResponse<T> {
  if (Array.isArray(raw)) {
    return { data: raw as T[], total: raw.length };
  }
  const obj = raw as { data?: unknown; total?: number } | null | undefined;
  if (obj && Array.isArray(obj.data)) {
    return { data: obj.data as T[], total: obj.total ?? 0 };
  }
  // Laravel 分页：data 是 { data: [], total: 0, ... }
  if (obj && obj.data && typeof obj.data === 'object') {
    const inner = obj.data as { data?: unknown[]; total?: number };
    if (Array.isArray(inner.data)) {
      return { data: inner.data as T[], total: inner.total ?? obj.total ?? 0 };
    }
  }
  return { data: [], total: 0 };
}

/**
 * 专门为 liveProspect 解包：保留 fans_data / province_data / province 字段
 *   后端响应：{ data: { data, total, ... }, fans_data, province_data, province, ... }
 *   扁平响应：{ data, total, fans_data, province_data, province, ... }
 */
function unwrapProspectPaginated(raw: unknown): LiveProspectListResponse {
  const unwrapped = unwrapLaravelPaginated<LiveReachUser>(raw);
  const obj = raw as
    | { fans_data?: LiveFansRing[]; province_data?: LiveProvinceItem[]; province?: string[] }
    | null
    | undefined;
  // Laravel 分页时 fans_data / province_data 在外层（而非嵌套 data 里）
  if (obj) {
    return {
      data: unwrapped.data,
      total: unwrapped.total,
      fans_data: obj.fans_data || [],
      province_data: obj.province_data || [],
      province: obj.province || [],
    };
  }
  return {
    data: unwrapped.data,
    total: unwrapped.total,
    fans_data: [],
    province_data: [],
    province: [],
  };
}

/* -------------------- 直播复盘 -------------------- */

export const liveReviewApi = {
  /** 直播复盘 - 头部账号/累计数据 POST /rpa/liveVideo */
  header: (data: LiveRpaParams) => apiPost('/rpa/liveVideo', data) as Promise<LiveReviewHeader>,

  /** 直播复盘 - 场次列表 POST /rpa/liveList */
  list: (data: LiveRpaParams) => apiPost('/rpa/liveList', data) as Promise<LiveReviewListResponse>,
};

/* -------------------- 直播账号来源 -------------------- */

export const liveSourceApi = {
  list: () => apiPost('/rpa/appList', {}) as Promise<LiveSourceGroup[]>,
};

/**
 * 按平台（dy / xhs / kuaishou）拉取直播账号列表
 * 新接口渠道: GET /channels/accounts-by-source?source=dy
 * 响应结构：{ code: 0, msg: 'ok', data: { list: LiveAccountItem[] } }
 * 拦截器已自动解包一层，调用方拿到的是 { list: LiveAccountItem[] }
 */
export interface LiveAccountListResponse {
  list: LiveAccountItem[];
}

export const liveAccountApi = {
  /** 按 source 平台查询直播账号 GET /channels/accounts-by-source?source={source} */
  bySource: (source: string) =>
    apiGet('/channels/accounts-by-source', { source }) as Promise<LiveAccountListResponse>,
};

/* -------------------- 直播营销管理（newFans / bulletChat / smartReply / liveCustomer） -------------------- */

export interface LiveSwitchListParams {
  source: string;
  source_app_id: string;
  switch_field?: string;
  value?: number;
}

export const liveConfigApi = {
  /** 拉取当前账号下全部开关值 + RPA 状态 POST /rpa/switchList */
  switchList: (data: LiveSwitchListParams) =>
    apiPost('/rpa/switchList', data) as Promise<LiveRpaSwitchListResponse>,

  /** 通用配置列表（弹幕语料 / 直播潜客 / 评论场景 / 监控词等） POST /rpa/index */
  index: async (data: LiveListParams) =>
    unwrapLaravelPaginated<LiveConfigRecord>(await apiPost('/rpa/index', data)),

  /** 通用添加配置 POST /rpa/add */
  add: (data: Record<string, unknown>) => apiPost('/rpa/add', data),

  /** 通用编辑配置 POST /rpa/edit */
  edit: (data: Record<string, unknown>) => apiPost('/rpa/edit', data),

  /** 通用删除配置 POST /rpa/delete */
  delete: (data: { type: number; id: number }) => apiPost('/rpa/delete', data),

  /** 智能回评回复条数 POST /rpa/getVideoCommentLimit */
  commentLimit: (data: LiveSwitchListParams) =>
    apiPost('/rpa/getVideoCommentLimit', data) as Promise<LiveReplyLimit>,
};

/** 新粉营销 / 直播潜客 触达用户列表 POST /rpa/newFansList */
export const liveNewFansApi = {
  list: async (data: LiveListParams) =>
    unwrapLaravelPaginated<LiveReachUser>(await apiPost('/rpa/newFansList', data)),
};

/** AI 弹幕 - 直播间列表 POST /rpa/getRoomList */
export const liveRoomApi = {
  list: async (data: LiveListParams) =>
    unwrapLaravelPaginated<LiveRoomItem>(await apiPost('/rpa/getRoomList', data)),
};

/** 直播潜客 - 触达用户列表 + 图表数据 POST /rpa/prospectList */
export interface LiveProspectListResponse {
  data: LiveReachUser[];
  total: number;
  fans_data: LiveFansRing[];
  province_data: LiveProvinceItem[];
  province: string[];
}
export const liveProspectApi = {
  list: async (data: LiveListParams) =>
    unwrapProspectPaginated(await apiPost('/rpa/prospectList', data)),
};

/** 评论场景 / 监控词 列表（卡片网格） POST /rpa/index */
export const liveCommentSceneApi = {
  list: async (data: LiveListParams) =>
    unwrapLaravelPaginated<LiveCommentScene>(await apiPost('/rpa/index', data)),
};

/** AI 弹幕详情 - 弹幕内容列表 POST /rpa/damuList */
export const liveDamuApi = {
  list: async (data: LiveDamuParams) =>
    unwrapLaravelPaginated<LiveDamuItem>(await apiPost('/rpa/damuList', data)),
};

/** 直播潜客 - 用户互动时间轴 POST /rpa/userTimeInteract */
export const liveUserTimelineApi = {
  get: (data: LiveUserTimelineParams) =>
    apiPost('/rpa/userTimeInteract', data) as Promise<LiveUserTimeline>,
};

/** 短视频潜客营销 - 触达用户列表 POST /rpa/getVideoProspect */
export const liveVideoProspectApi = {
  list: async (data: LiveListParams) =>
    unwrapLaravelPaginated<LiveVideoProspectUser>(await apiPost('/rpa/getVideoProspect', data)),
};

/* -------------------- 直播统计 -------------------- */

/** 直播统计 - 表头（列定义） POST /customized/header
 *  接口载荷：{ api: '/v2/api/customer_header', script: 'SubServiceV2', type: 5 }
 *  响应解包后 data 直接为 LiveStatColumn[] */
export const liveStatApi = {
  /** 获取列定义 */
  header: (data: { api: string; script: string; type: number }) =>
    apiPost('/customized/header', data) as Promise<LiveStatColumnList>,

  /** 获取列表数据 POST /project/stat（拦截器已解外层，返回值即为行数组） */
  list: (data: LiveStatParams) => apiPost('/project/stat', data) as Promise<LiveStatListResponse>,

  /** 触发导出 POST /project/stat（传 export:1，拦截器已解外层，返回 { url }） */
  export: (data: LiveStatParams) =>
    apiPost('/project/stat', data) as Promise<LiveStatExportResponse>,
};
